// lib
import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  getDefaultViewMode,
  getViewModeList,
  getDataExportOptions,
  isShowExport,
  isShowHead
} from '@aytm/app/stats/page/question-views/md/lib/controls-utils'

// components
import { Dropdown } from 'primereact/dropdown'
import { optionRow } from '@aytm/app/stats/page/question-views/components/controls/OptionLabel'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'

import ControlsHead from '@aytm/app/stats/page/question-views/md/components/ControlsHead'
import ControlHelpContent from '@aytm/app/stats/page/question-views/md/components/ControlsHelpContent'

// styles
import style from '@aytm/app/stats/page/question-views/components/controls/styles/Controls.module.scss'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const ControlsMD = ({
  features,
  link,
  format,
  answer,
  viewMode,
  itemsPerSet,
  maxDiffType,
  isReportXpert,
  specialExport,
  onViewModeChange,
  onExport
}) => {
  const options = useMemo(() => getViewModeList(itemsPerSet), [itemsPerSet])

  const resolvedViewMode = viewMode || getDefaultViewMode(format, answer)

  const exportOptions = getDataExportOptions({
    itemsPerSet,
    specialExport,
    featureTurfIsEnabled: !features.turf.disabled
  })

  const showHead = isShowHead({ maxDiffType, answer })

  const showExport = isShowExport({
    link,
    featureAuthorizedExportsIsEnabled: !features.authorized_exports.disabled,
    maxDiffType,
    isReportXpert
  })

  // The dropdown hands back an option key; the Backbone handler expects the resolved descriptor.
  const onExportClick = (key, callback, event) => {
    const option = exportOptions.find(o => o.key === key)
    if (!option) {
      callback?.()
      return
    }

    onExport(
      {
        event,
        fileType: option.fileType,
        exportType: option.exportType,
        exportMethod: option.exportMethod,
        exportMethodText: option.exportMethodText
      },
      callback
    )
  }

  const onChange = event => onViewModeChange(event.value)

  return (
    <>
      {showHead ? <ControlsHead maxDiffType={maxDiffType} /> : null}
      <div className={`${style.component} ${NO_PRINT}`}>
        <div className="p-fluid p-col-4">
          <Dropdown
            className="p-dropdown-rounded"
            aria-label="View mode"
            ariaLabel="View mode"
            value={resolvedViewMode}
            options={options}
            optionValue="value"
            optionLabel="text"
            optionDisabled="disabled"
            fixedDropdownDir="bottom"
            itemTemplate={optionRow('md-view-mode')}
            onChange={onChange}
          />
        </div>
        <Popup
          width="400px"
          trigger="click"
          direction="righttop"
          popupOffset={9}
          hideOtherPopupsOnShow
          content={() => <ControlHelpContent itemsPerSet={itemsPerSet} maxDiffType={maxDiffType} />}
        >
          <i className="icon-aytmfd-help-v2" />
        </Popup>
        {showExport ? (
          <div className="p-right">
            <ExportDropdown
              text={`Download Individual Statistics${!features.turf.disabled ? ' & TURF ' : ''}`}
              options={exportOptions}
              onExport={onExportClick}
            />
          </div>
        ) : null}
      </div>
    </>
  )
}

ControlsMD.propTypes = {
  features: PropTypes.object,
  link: PropTypes.string,
  format: PropTypes.object,
  answer: answerPropType,
  viewMode: PropTypes.string,
  itemsPerSet: PropTypes.number,
  maxDiffType: PropTypes.number,
  specialExport: PropTypes.object,
  isReportXpert: PropTypes.bool,
  onViewModeChange: PropTypes.func,
  onExport: PropTypes.func
}

ControlsMD.displayName = 'ControlsMD'

export default ControlsMD
